import { useEffect, useMemo, useState } from "react"
import { useCustomizationFields } from "components/ui/customWebviewer/shared/CustomizationSection/useCustomizationFields.hook"
import type { TEref } from "types/elevationReferences.type"

/**
 * Form state of the "add other object" panel (REQ-299). Nothing here is persisted as it changes:
 * unlike the edit sidebar, which commits every field on blur, this form is only read once, when
 * Create object is pressed.
 *
 * There is no category state at all. An "Other" object is BY DEFINITION the one with no category on
 * any level (see `isOtherFurniture`), so the panel shows the fixed "Other" top level and the mode
 * posts `related_category_id: null` — nothing for the user to pick, nothing for this hook to track.
 *
 * `pageErefs` are the elevation references of the page the area was drawn on. Erefs are page-bound
 * and the endpoint rejects a cross-page link, so a re-draw on another page has to drop a selection
 * that no longer belongs.
 */
export const useCreateOtherForm = (pageErefs: TEref[]) => {
	const [selectedErefId, setSelectedErefId] = useState<number | null>(null)
	const [width, setWidth] = useState<number | null>(null)
	const [height, setHeight] = useState<number | null>(null)
	const [depth, setDepth] = useState<number | null>(null)
	const [name, setName] = useState("")
	const [price, setPrice] = useState<number | null>(null)
	// REQ-413/414. Read once, at Create, like everything else here — the customization is applied
	// by a PUT chained onto the create, because its endpoint is per-object.
	const customization = useCustomizationFields()

	// Drop an elevation that isn't on the page the area now sits on (a re-draw can move it).
	useEffect(() => {
		if (selectedErefId === null) return
		if (pageErefs.some((eref) => eref.id === selectedErefId)) return
		setSelectedErefId(null)
	}, [pageErefs, selectedErefId])

	const selectedEref = useMemo(
		() => pageErefs.find((eref) => eref.id === selectedErefId) ?? null,
		[pageErefs, selectedErefId],
	)

	// The endpoint itself only insists on the area and the page; everything below is the product rule
	// the design states with its required marks. Dimensions must be GREATER than zero, not merely
	// filled — inches of a real object, where 0 is not a smaller object but a broken one (#1319
	// review). Name and price are the two the backend insists on anyway, later, when the object is
	// moved into the quotation.
	const isValid =
		width !== null &&
		width > 0 &&
		height !== null &&
		height > 0 &&
		depth !== null &&
		depth > 0 &&
		name.trim().length > 0 &&
		price !== null

	return {
		selectedErefId,
		setSelectedErefId,
		selectedEref,
		width,
		setWidth,
		height,
		setHeight,
		depth,
		setDepth,
		name,
		setName,
		price,
		setPrice,
		customization,
		isValid,
	}
}
